/**
 * Geometry OPERATION API (Phase 7) — the only way authoritative geometry
 * changes. Pure and deterministic: each operation takes a PlanModel and
 * returns a new PlanModel plus an explicit CHANGESET (before/after per
 * entity). The changeset is what undo/redo inverts, what persistence
 * upserts, and what future AI/import/collaboration will replay. Pointer
 * handlers only translate gestures into these calls.
 *
 * Invalid results are REFUSED with a GeometryError (never silently applied):
 * zero-length walls, duplicate walls, openings that would fall off their
 * wall or be cut by a split, coordinates outside the safe extent.
 *
 * Deletion is ARCHIVAL (archivedAt); identities survive for Phase 9.
 */
import type { DoorSwing, ObjectGeometryType, OpeningKind, PlanModel, PlanNode, PlanObject, Point, RoomAnchor, Wall, WallKind, WallOpening, WindowConfiguration, WindowOperation } from "./model";
import { activeOpenings, activeWalls, nodeOf, wallOf } from "./model";
import { objectPose, objectRunLength } from "./objects";
import { MAX_COORDINATE_PU, PU_PER_INCH } from "./units";
import { analyzePlan, type PlanAnalysis } from "./analysis";
import { reconcileRoomAnchors, type AnchorState } from "./roomBinding";
import { closestPointOnSegment, collinear, distance, pointAlong, pointOnSegment, pointsEqual, segmentIntersection, sub } from "./vector";

export class GeometryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GeometryError";
  }
}

export type EntityChange =
  | { kind: "node"; id: string; before: PlanNode | null; after: PlanNode | null }
  | { kind: "wall"; id: string; before: Wall | null; after: Wall | null }
  | { kind: "opening"; id: string; before: WallOpening | null; after: WallOpening | null }
  | { kind: "object"; id: string; before: PlanObject | null; after: PlanObject | null }
  | { kind: "anchor"; id: string; before: RoomAnchor | null; after: RoomAnchor | null };

export interface OpContext {
  newId: () => string;
  now: () => string;
}

export interface OpResult {
  model: PlanModel;
  changes: EntityChange[];
}

/** Points closer than this (pu) are treated as the same location when joining. */
export const JOIN_TOLERANCE_PU = 2;

/** Default thicknesses. */
export const INTERIOR_WALL_THICKNESS_PU = Math.round(4.5 * PU_PER_INCH); // 9144
export const EXTERIOR_WALL_THICKNESS_PU = 6 * PU_PER_INCH; // 12192

// ---------------------------------------------------------------------------
// Draft: mutable working copy that records minimal before/after changes.
// ---------------------------------------------------------------------------
class Draft {
  nodes: Map<string, PlanNode>;
  walls: Map<string, Wall>;
  openings: Map<string, WallOpening>;
  objects: Map<string, PlanObject>;
  anchors: Map<string, RoomAnchor>;
  private readonly before = new Map<string, EntityChange>();

  constructor(private readonly original: PlanModel) {
    this.nodes = new Map(original.nodes);
    this.walls = new Map(original.walls);
    this.openings = new Map(original.openings);
    this.objects = new Map(original.objects);
    this.anchors = new Map(original.anchors);
  }

  private track(kind: EntityChange["kind"], id: string): void {
    const key = `${kind}:${id}`;
    if (this.before.has(key)) return;
    const orig =
      kind === "node"
        ? this.original.nodes.get(id) ?? null
        : kind === "wall"
          ? this.original.walls.get(id) ?? null
          : kind === "opening"
            ? this.original.openings.get(id) ?? null
            : kind === "object"
              ? this.original.objects.get(id) ?? null
              : this.original.anchors.get(id) ?? null;
    this.before.set(key, { kind, id, before: orig as never, after: null });
  }

  setNode(n: PlanNode): void {
    this.track("node", n.id);
    this.nodes.set(n.id, n);
  }
  setWall(w: Wall): void {
    this.track("wall", w.id);
    this.walls.set(w.id, w);
  }
  setOpening(o: WallOpening): void {
    this.track("opening", o.id);
    this.openings.set(o.id, o);
  }
  setObject(o: PlanObject): void {
    this.track("object", o.id);
    this.objects.set(o.id, o);
  }
  object(id: string): PlanObject {
    const o = this.objects.get(id);
    if (!o || o.archivedAt !== null) throw new GeometryError("That object no longer exists.");
    return o;
  }
  activeObjects(): PlanObject[] {
    return [...this.objects.values()].filter((o) => o.archivedAt === null);
  }
  hostedOn(wallId: string): PlanObject[] {
    return this.activeObjects().filter((o) => o.hostWallId === wallId);
  }
  setAnchor(a: RoomAnchor): void {
    this.track("anchor", a.roomId);
    this.anchors.set(a.roomId, a);
  }
  removeAnchor(roomId: string): void {
    this.track("anchor", roomId);
    this.anchors.delete(roomId);
  }

  model(): PlanModel {
    return {
      designId: this.original.designId,
      floorId: this.original.floorId,
      nodes: this.nodes,
      walls: this.walls,
      openings: this.openings,
      objects: this.objects,
      anchors: this.anchors,
    };
  }

  result(): OpResult {
    const changes: EntityChange[] = [];
    for (const [key, c] of this.before) {
      const id = c.id;
      const after =
        c.kind === "node"
          ? this.nodes.get(id) ?? null
          : c.kind === "wall"
            ? this.walls.get(id) ?? null
            : c.kind === "opening"
              ? this.openings.get(id) ?? null
              : c.kind === "object"
                ? this.objects.get(id) ?? null
                : this.anchors.get(id) ?? null;
      if (c.before === after) continue;
      if (c.before && after && JSON.stringify(c.before) === JSON.stringify(after)) continue;
      void key;
      changes.push({ ...c, after } as EntityChange);
    }
    return { model: this.model(), changes };
  }

  node(id: string): PlanNode {
    const n = this.nodes.get(id);
    if (!n || n.archivedAt !== null) throw new GeometryError("That point no longer exists.");
    return n;
  }
  wall(id: string): Wall {
    const w = this.walls.get(id);
    if (!w || w.archivedAt !== null) throw new GeometryError("That wall no longer exists.");
    return w;
  }
  activeWalls(): Wall[] {
    return [...this.walls.values()].filter((w) => w.archivedAt === null);
  }
  activeNodes(): PlanNode[] {
    return [...this.nodes.values()].filter((n) => n.archivedAt === null);
  }
  openingsOf(wallId: string): WallOpening[] {
    return [...this.openings.values()].filter((o) => o.archivedAt === null && o.wallId === wallId);
  }
  wallsAt(nodeId: string): Wall[] {
    return this.activeWalls().filter((w) => w.startNodeId === nodeId || w.endNodeId === nodeId);
  }
  wallLength(w: Wall): number {
    return distance(this.node(w.startNodeId), this.node(w.endNodeId));
  }
}

function assertInBounds(p: Point): void {
  if (!Number.isInteger(p.x) || !Number.isInteger(p.y)) throw new GeometryError("Coordinates must be whole plan units.");
  if (Math.abs(p.x) > MAX_COORDINATE_PU || Math.abs(p.y) > MAX_COORDINATE_PU) {
    throw new GeometryError("That point is too far from the plan origin (limit ±410 ft).");
  }
}

function assertWallValid(d: Draft, w: Wall): void {
  const a = d.node(w.startNodeId);
  const b = d.node(w.endNodeId);
  if (a.id === b.id || pointsEqual(a, b)) throw new GeometryError("A wall cannot have zero length.");
  const len = distance(a, b);
  for (const o of d.openingsOf(w.id)) {
    if (o.offsetFromStart < 0 || o.offsetFromStart + o.width > len + 0.5) {
      throw new GeometryError(`The ${o.kind} on this wall would no longer fit (wall would be too short).`);
    }
  }
}

/** Validate every wall touching a set of nodes (after a move). */
function assertNodesValid(d: Draft, nodeIds: Iterable<string>): void {
  const seen = new Set<string>();
  for (const nid of nodeIds) {
    for (const w of d.wallsAt(nid)) {
      if (seen.has(w.id)) continue;
      seen.add(w.id);
      assertWallValid(d, w);
    }
  }
}

// ---------------------------------------------------------------------------
// Node resolution / splitting helpers
// ---------------------------------------------------------------------------
function findNodeAt(d: Draft, p: Point, tolerance = JOIN_TOLERANCE_PU): PlanNode | null {
  let best: PlanNode | null = null;
  let bestD = tolerance + 1e-9;
  for (const n of d.activeNodes()) {
    const dist = distance(n, p);
    if (dist <= bestD) {
      bestD = dist;
      best = n;
    }
  }
  return best;
}

function createNode(d: Draft, ctx: OpContext, p: Point): PlanNode {
  assertInBounds(p);
  const n: PlanNode = { id: ctx.newId(), floorId: d.model().floorId, x: p.x, y: p.y, originId: null, archivedAt: null };
  d.setNode(n);
  return n;
}

/** Split wall at `point` (projected onto it); returns the junction node and the new second segment. */
function splitWallAt(d: Draft, ctx: OpContext, wallId: string, point: Point): { node: PlanNode; first: Wall; second: Wall } {
  const w = d.wall(wallId);
  const a = d.node(w.startNodeId);
  const b = d.node(w.endNodeId);
  const c = closestPointOnSegment(point, a, b);
  if (pointsEqual(c.point, a) || pointsEqual(c.point, b)) throw new GeometryError("Cannot split a wall at its end.");
  const splitDist = distance(a, c.point);
  const len = distance(a, b);
  // openings: keep / move / refuse
  const moves: WallOpening[] = [];
  for (const o of d.openingsOf(w.id)) {
    const oStart = o.offsetFromStart;
    const oEnd = o.offsetFromStart + o.width;
    if (oEnd <= splitDist + 0.5) continue;
    if (oStart >= splitDist - 0.5) {
      moves.push(o);
      continue;
    }
    throw new GeometryError(`That would cut through a ${o.kind}. Move the ${o.kind} first.`);
  }
  const node = createNode(d, ctx, c.point);
  const second: Wall = { ...w, id: ctx.newId(), startNodeId: node.id, endNodeId: w.endNodeId };
  const first: Wall = { ...w, endNodeId: node.id };
  d.setWall(first);
  d.setWall(second);
  for (const o of moves) {
    d.setOpening({ ...o, wallId: second.id, offsetFromStart: Math.round(o.offsetFromStart - splitDist) });
  }
  // hosted objects (outlets/switches) follow the segment their point falls on
  for (const obj of d.hostedOn(w.id)) {
    const off = obj.hostOffset ?? 0;
    if (off > splitDist) d.setObject({ ...obj, hostWallId: second.id, hostOffset: Math.round(off - splitDist) });
  }
  void len;
  return { node, first, second };
}

/** Resolve a point to a node: existing node, split of a wall it lies on, or a new node. */
function resolveEndpoint(d: Draft, ctx: OpContext, p: Point, excludeWallIds: Set<string>): PlanNode {
  const existing = findNodeAt(d, p);
  if (existing) return existing;
  for (const w of d.activeWalls()) {
    if (excludeWallIds.has(w.id)) continue;
    const a = d.node(w.startNodeId);
    const b = d.node(w.endNodeId);
    if (pointOnSegment(p, a, b, JOIN_TOLERANCE_PU)) {
      return splitWallAt(d, ctx, w.id, p).node;
    }
  }
  return createNode(d, ctx, p);
}

// ---------------------------------------------------------------------------
// Public operations
// ---------------------------------------------------------------------------
export interface WallProps {
  /** Physical wall (default) or zero-thickness logical room boundary. */
  kind?: WallKind;
  thickness: number;
  heightInches?: number | null;
  exteriorOverride?: boolean | null;
  notes?: string;
}

export type EndpointRef = { nodeId: string } | { point: Point };

export interface CreateWallResult extends OpResult {
  /** Segments created along the requested line, in order start → end. */
  wallIds: string[];
  startNodeId: string;
  endNodeId: string;
}

/**
 * Create a wall from start to end. Endpoints join existing nodes, split
 * walls they land on (T-junctions), and the new line is split at every
 * node it passes through and every wall it crosses (X-intersections).
 */
export function createWall(model: PlanModel, ctx: OpContext, start: EndpointRef, end: EndpointRef, props: WallProps): CreateWallResult {
  const kind: WallKind = props.kind ?? "wall";
  if (kind === "boundary") {
    if (props.thickness !== 0) throw new GeometryError("A room boundary has no thickness.");
  } else if (props.thickness <= 0) {
    throw new GeometryError("Wall thickness must be positive.");
  }
  const d = new Draft(model);
  const exclude = new Set<string>();
  const startNode = "nodeId" in start ? d.node(start.nodeId) : resolveEndpoint(d, ctx, start.point, exclude);
  const endNode = "nodeId" in end ? d.node(end.nodeId) : resolveEndpoint(d, ctx, end.point, exclude);
  if (startNode.id === endNode.id || pointsEqual(startNode, endNode)) throw new GeometryError("A wall cannot have zero length.");
  const A = { x: startNode.x, y: startNode.y };
  const B = { x: endNode.x, y: endNode.y };
  const lengthAB = distance(A, B);
  // Refuse overlap with collinear existing walls.
  for (const w of d.activeWalls()) {
    const a = d.node(w.startNodeId);
    const b = d.node(w.endNodeId);
    if (!collinear(sub(B, A), sub(b, a))) continue;
    if (!pointOnSegment(a, A, B, JOIN_TOLERANCE_PU) && !pointOnSegment(b, A, B, JOIN_TOLERANCE_PU) && !pointOnSegment(A, a, b, JOIN_TOLERANCE_PU)) continue;
    // collinear with overlapping span?
    const ta = ((a.x - A.x) * (B.x - A.x) + (a.y - A.y) * (B.y - A.y)) / (lengthAB * lengthAB);
    const tb = ((b.x - A.x) * (B.x - A.x) + (b.y - A.y) * (B.y - A.y)) / (lengthAB * lengthAB);
    const lo = Math.min(ta, tb);
    const hi = Math.max(ta, tb);
    const overlap = Math.min(hi, 1) - Math.max(lo, 0);
    if (overlap * lengthAB > JOIN_TOLERANCE_PU) throw new GeometryError("That wall would overlap an existing wall.");
  }
  // Collect split points along A→B: existing nodes on the open segment + crossings.
  const splits: Array<{ t: number; nodeId: string }> = [];
  for (const n of d.activeNodes()) {
    if (n.id === startNode.id || n.id === endNode.id) continue;
    if (pointOnSegment(n, A, B, JOIN_TOLERANCE_PU)) {
      const t = distance(A, n) / lengthAB;
      if (t > 0 && t < 1) splits.push({ t, nodeId: n.id });
    }
  }
  for (const w of d.activeWalls().slice()) {
    if (w.startNodeId === startNode.id || w.endNodeId === startNode.id || w.startNodeId === endNode.id || w.endNodeId === endNode.id) continue;
    const a = d.node(w.startNodeId);
    const b = d.node(w.endNodeId);
    const x = segmentIntersection(A, B, a, b);
    if (!x) continue;
    const existing = findNodeAt(d, x.point);
    if (existing) {
      if (!splits.some((s) => s.nodeId === existing.id)) splits.push({ t: x.t, nodeId: existing.id });
      continue;
    }
    const { node } = splitWallAt(d, ctx, w.id, x.point);
    splits.push({ t: x.t, nodeId: node.id });
  }
  splits.sort((p, q) => p.t - q.t);
  const chain: string[] = [startNode.id];
  for (const s of splits) if (chain[chain.length - 1] !== s.nodeId) chain.push(s.nodeId);
  if (chain[chain.length - 1] !== endNode.id) chain.push(endNode.id);
  const wallIds: string[] = [];
  for (let i = 0; i + 1 < chain.length; i++) {
    const s = chain[i];
    const e = chain[i + 1];
    if (d.activeWalls().some((w) => (w.startNodeId === s && w.endNodeId === e) || (w.startNodeId === e && w.endNodeId === s))) {
      throw new GeometryError("A wall already connects those two points.");
    }
    const wall: Wall = {
      id: ctx.newId(),
      floorId: model.floorId,
      kind,
      startNodeId: s,
      endNodeId: e,
      thickness: props.thickness,
      heightInches: props.heightInches ?? null,
      exteriorOverride: props.exteriorOverride ?? null,
      originId: null,
      notes: props.notes ?? "",
      archivedAt: null,
    };
    d.setWall(wall);
    assertWallValid(d, wall);
    wallIds.push(wall.id);
  }
  return { ...d.result(), wallIds, startNodeId: startNode.id, endNodeId: endNode.id };
}

export type RectangleReference = "center" | "outer" | "inner";

/**
 * Rectangle Room tool: four connected walls. `width`/`height` are measured
 * on the reference line (outer face by default for exterior footprints);
 * the authoritative centerline is derived from it.
 */
export function createRectangle(
  model: PlanModel,
  ctx: OpContext,
  params: { corner: Point; width: number; height: number; reference: RectangleReference } & WallProps,
): OpResult & { wallIds: string[] } {
  if (params.width <= 0 || params.height <= 0) throw new GeometryError("Rectangle sizes must be positive.");
  const half = Math.round(params.thickness / 2);
  const inset = params.reference === "outer" ? half : params.reference === "inner" ? -half : 0;
  const x0 = params.corner.x + inset;
  const y0 = params.corner.y + inset;
  const x1 = params.corner.x + params.width - inset;
  const y1 = params.corner.y + params.height - inset;
  if (x1 - x0 <= 0 || y1 - y0 <= 0) throw new GeometryError("Rectangle is too small for the wall thickness.");
  const corners: Point[] = [
    { x: x0, y: y0 },
    { x: x1, y: y0 },
    { x: x1, y: y1 },
    { x: x0, y: y1 },
  ];
  let current = model;
  const changes: EntityChange[] = [];
  const wallIds: string[] = [];
  for (let i = 0; i < 4; i++) {
    const r = createWall(current, ctx, { point: corners[i] }, { point: corners[(i + 1) % 4] }, params);
    current = r.model;
    changes.push(...r.changes);
    wallIds.push(...r.wallIds);
  }
  return { model: current, changes: mergeChanges(changes), wallIds };
}

/** Collapse a sequence of changesets into minimal before/after per entity. */
export function mergeChanges(changes: EntityChange[]): EntityChange[] {
  const map = new Map<string, EntityChange>();
  for (const c of changes) {
    const key = `${c.kind}:${c.id}`;
    const prev = map.get(key);
    if (prev) map.set(key, { ...prev, after: c.after } as EntityChange);
    else map.set(key, c);
  }
  return [...map.values()].filter((c) => JSON.stringify(c.before) !== JSON.stringify(c.after));
}

export function moveNode(model: PlanModel, ctx: OpContext, nodeId: string, to: Point): OpResult {
  void ctx;
  assertInBounds(to);
  const d = new Draft(model);
  const n = d.node(nodeId);
  d.setNode({ ...n, x: to.x, y: to.y });
  assertNodesValid(d, [nodeId]);
  return d.result();
}

/** Translate a set of walls (e.g. one wall or a whole run) by delta; shared nodes move once. */
export function moveWalls(model: PlanModel, ctx: OpContext, wallIds: string[], delta: Point): OpResult {
  void ctx;
  const d = new Draft(model);
  const nodeIds = new Set<string>();
  for (const id of wallIds) {
    const w = d.wall(id);
    nodeIds.add(w.startNodeId);
    nodeIds.add(w.endNodeId);
  }
  for (const nid of nodeIds) {
    const n = d.node(nid);
    const to = { x: n.x + delta.x, y: n.y + delta.y };
    assertInBounds(to);
    d.setNode({ ...n, x: to.x, y: to.y });
  }
  assertNodesValid(d, nodeIds);
  return d.result();
}

/**
 * SLIDE ONE WALL AND REPAIR WHAT IT LEAVES BEHIND (spec §60E).
 *
 * `moveWalls` moves the wall's NODES, and because a corner node belongs to
 * every wall that meets there, the whole corner travels: dragging one wall
 * dragged the house. That is right for "move this run", and wrong for what
 * a person means by dragging a wall.
 *
 * A slide keeps the surrounding structure exactly where it is:
 *
 *      A────────B          A┄┄┄┄┄┄┄┄B      A and B stay; the wall moves;
 *      │        │    →     │        │      the gaps it left become ordinary
 *      │        │          A'───────B'     connector walls.
 *
 * Deterministic, in this order, for each end of the wall:
 *
 *   1. an end with nothing else attached simply moves — nothing to repair;
 *   2. otherwise the wall detaches onto a node at the new position, REUSING
 *      an existing node there, or splitting a wall that runs through it, so
 *      a slide that lands on existing geometry joins it instead of stacking
 *      on top of it;
 *   3. the old junction is reconnected with one connector wall — unless
 *      that connector would be zero length, would duplicate a wall that is
 *      already there, or would lie along the moved wall itself (a slide
 *      along its own axis), in which case the moved wall is split at the old
 *      junction so nothing is left dangling.
 *
 * Openings ride on their host wall, which keeps its id, its length and its
 * direction, so they need no adjustment; openings on the neighbours are not
 * touched at all. Connectors are ordinary walls with stable ids, editable
 * and deletable like any other. Everything is one changeset, so it is one
 * undo.
 */
export interface SlideWallResult extends OpResult {
  /** Connector walls created to close the gaps (may be empty). */
  connectorWallIds: string[];
  /** Junctions that could not be reconnected, e.g. an opening blocked a split. */
  unresolvedNodeIds: string[];
}

export function slideWall(model: PlanModel, ctx: OpContext, wallId: string, delta: Point): SlideWallResult {
  const d = new Draft(model);
  const wall = d.wall(wallId);
  if (delta.x === 0 && delta.y === 0) return { model, changes: [], connectorWallIds: [], unresolvedNodeIds: [] };

  const ends: Array<"start" | "end"> = ["start", "end"];
  const original = {
    start: { ...d.node(wall.startNodeId) },
    end: { ...d.node(wall.endNodeId) },
  };
  const moved = {
    start: { x: original.start.x + delta.x, y: original.start.y + delta.y },
    end: { x: original.end.x + delta.x, y: original.end.y + delta.y },
  };
  assertInBounds(moved.start);
  assertInBounds(moved.end);

  const connectorWallIds: string[] = [];
  const unresolvedNodeIds: string[] = [];
  const touched = new Set<string>();
  const props: WallProps = {
    kind: wall.kind,
    thickness: wall.thickness,
    heightInches: wall.heightInches,
    exteriorOverride: wall.exteriorOverride,
    notes: "",
  };

  for (const end of ends) {
    const node = original[end];
    const target = moved[end];
    const attached = d.wallsAt(node.id).filter((w) => w.id !== wallId && w.archivedAt === null);

    if (attached.length === 0) {
      // A free end: moving it IS the slide, and there is nothing to repair.
      d.setNode({ ...d.node(node.id), x: target.x, y: target.y });
      touched.add(node.id);
      continue;
    }

    // Detach: the wall gets its own node at the new position, and the old
    // junction is left holding its other walls exactly as they were.
    const landing = resolveEndpoint(d, ctx, target, new Set([wallId]));
    const current = d.wall(wallId);
    d.setWall(end === "start" ? { ...current, startNodeId: landing.id } : { ...current, endNodeId: landing.id });
    touched.add(landing.id);
    touched.add(node.id);

    if (landing.id === node.id) continue; // it landed back on its own junction

    const gap = distance(node, landing);
    if (gap === 0) continue;
    if (d.wallsAt(node.id).some((w) => w.startNodeId === landing.id || w.endNodeId === landing.id)) {
      continue; // a wall already joins these two — never duplicate it
    }

    // A slide along the wall's own direction leaves the old junction sitting
    // ON the moved wall: split it there instead of drawing a connector on
    // top of it.
    const a = d.node(d.wall(wallId).startNodeId);
    const b = d.node(d.wall(wallId).endNodeId);
    if (pointOnSegment(node, a, b, JOIN_TOLERANCE_PU)) {
      try {
        splitWallAt(d, ctx, wallId, { x: node.x, y: node.y });
      } catch {
        // An opening sits where the split would fall: leave the junction
        // reported rather than cutting through a door.
        unresolvedNodeIds.push(node.id);
      }
      continue;
    }

    try {
      const created = createWallBetween(d, ctx, node.id, landing.id, props);
      connectorWallIds.push(created.id);
    } catch {
      unresolvedNodeIds.push(node.id);
    }
  }

  assertNodesValid(d, touched);
  return { ...d.result(), connectorWallIds, unresolvedNodeIds };
}

/** One wall between two existing nodes, refusing zero length and duplicates. */
function createWallBetween(d: Draft, ctx: OpContext, startNodeId: string, endNodeId: string, props: WallProps): Wall {
  if (startNodeId === endNodeId) throw new GeometryError("A wall needs two different ends.");
  const wall: Wall = {
    id: ctx.newId(),
    floorId: d.model().floorId,
    kind: props.kind ?? "wall",
    startNodeId,
    endNodeId,
    thickness: props.kind === "boundary" ? 0 : props.thickness,
    heightInches: props.heightInches ?? null,
    exteriorOverride: props.exteriorOverride ?? null,
    originId: null,
    notes: props.notes ?? "",
    archivedAt: null,
  };
  d.setWall(wall);
  assertWallValid(d, wall);
  return wall;
}

/**
 * Set a wall's length. The PINNED end stays; the other end moves along the
 * wall direction; walls sharing the moving node follow (stretch/shrink).
 */
export function setWallLength(model: PlanModel, ctx: OpContext, wallId: string, lengthPu: number, pinned: "start" | "end"): OpResult {
  void ctx;
  if (!Number.isInteger(lengthPu) || lengthPu <= 0) throw new GeometryError("Wall length must be positive.");
  const d = new Draft(model);
  const w = d.wall(wallId);
  const a = d.node(w.startNodeId);
  const b = d.node(w.endNodeId);
  const pinnedNode = pinned === "start" ? a : b;
  const moving = pinned === "start" ? b : a;
  const to = pointAlong(pinnedNode, moving, lengthPu);
  assertInBounds(to);
  d.setNode({ ...moving, x: to.x, y: to.y });
  assertNodesValid(d, [moving.id]);
  return d.result();
}

export function setWallThickness(model: PlanModel, ctx: OpContext, wallId: string, thickness: number): OpResult {
  void ctx;
  if (!Number.isInteger(thickness) || thickness <= 0) throw new GeometryError("Wall thickness must be positive.");
  const d = new Draft(model);
  if (d.wall(wallId).kind === "boundary") throw new GeometryError("A room boundary has no thickness.");
  d.setWall({ ...d.wall(wallId), thickness });
  return d.result();
}

export function setWallHeight(model: PlanModel, ctx: OpContext, wallId: string, heightInches: number | null): OpResult {
  void ctx;
  if (heightInches !== null && (!Number.isInteger(heightInches) || heightInches <= 0)) throw new GeometryError("Wall height must be positive.");
  const d = new Draft(model);
  d.setWall({ ...d.wall(wallId), heightInches });
  return d.result();
}

export function setWallExteriorOverride(model: PlanModel, ctx: OpContext, wallId: string, exteriorOverride: boolean | null): OpResult {
  void ctx;
  const d = new Draft(model);
  d.setWall({ ...d.wall(wallId), exteriorOverride });
  return d.result();
}

/** Split an existing wall at a point (explicit Split action). */
export function splitWall(model: PlanModel, ctx: OpContext, wallId: string, point: Point): OpResult & { nodeId: string; wallIds: [string, string] } {
  const d = new Draft(model);
  const { node, first, second } = splitWallAt(d, ctx, wallId, point);
  return { ...d.result(), nodeId: node.id, wallIds: [first.id, second.id] };
}

/** Archive a wall and its openings; orphaned nodes are archived too.
 *  Wall-hosted objects survive: their world pose is baked into x/y and the
 *  host link cleared (the device outlives the wall as a free object). */
export function archiveWall(model: PlanModel, ctx: OpContext, wallId: string): OpResult {
  const d = new Draft(model);
  const w = d.wall(wallId);
  const now = ctx.now();
  for (const obj of d.hostedOn(wallId)) {
    const pose = objectPose(d.model(), obj);
    d.setObject({ ...obj, x: pose.center.x, y: pose.center.y, rotationX10: Math.round(((pose.rotationRad * 180) / Math.PI) * 10), hostWallId: null, hostOffset: null, hostSide: null });
  }
  d.setWall({ ...w, archivedAt: now });
  for (const o of d.openingsOf(w.id)) d.setOpening({ ...o, archivedAt: now });
  for (const nid of [w.startNodeId, w.endNodeId]) {
    if (d.wallsAt(nid).length === 0) d.setNode({ ...d.node(nid), archivedAt: now });
  }
  return d.result();
}

/** Merge node `removeId` into `keepId` (Close gap). */
export function mergeNodes(model: PlanModel, ctx: OpContext, keepId: string, removeId: string): OpResult {
  if (keepId === removeId) throw new GeometryError("Those are the same point.");
  const d = new Draft(model);
  const keep = d.node(keepId);
  const remove = d.node(removeId);
  for (const w of d.wallsAt(removeId)) {
    const updated: Wall = {
      ...w,
      startNodeId: w.startNodeId === removeId ? keepId : w.startNodeId,
      endNodeId: w.endNodeId === removeId ? keepId : w.endNodeId,
    };
    if (updated.startNodeId === updated.endNodeId) throw new GeometryError("Closing that gap would collapse a wall to zero length.");
    const dup = d.activeWalls().some(
      (o) => o.id !== w.id && ((o.startNodeId === updated.startNodeId && o.endNodeId === updated.endNodeId) || (o.startNodeId === updated.endNodeId && o.endNodeId === updated.startNodeId)),
    );
    if (dup) throw new GeometryError("Closing that gap would create a duplicate wall.");
    d.setWall(updated);
  }
  d.setNode({ ...remove, archivedAt: ctx.now() });
  void keep;
  assertNodesValid(d, [keepId]);
  return d.result();
}

/** Close a detected gap: move/merge the dangling node onto its target (node or wall). */
export function closeGap(model: PlanModel, ctx: OpContext, nodeId: string, target: { nodeId: string } | { wallId: string; point: Point }): OpResult {
  if ("nodeId" in target) return mergeNodes(model, ctx, target.nodeId, nodeId);
  const d = new Draft(model);
  const wallOfNode = d.wallsAt(nodeId);
  if (wallOfNode.some((w) => w.id === target.wallId)) throw new GeometryError("That point is already on that wall.");
  const { node } = splitWallAt(d, ctx, target.wallId, target.point);
  const interim = d.result();
  const merged = mergeNodes(interim.model, ctx, node.id, nodeId);
  return { model: merged.model, changes: mergeChanges([...interim.changes, ...merged.changes]) };
}

// ---- Openings -------------------------------------------------------------
export interface OpeningInput {
  kind: OpeningKind;
  /** Offset from the wall's start node to the opening's near edge (pu). */
  offsetFromStart: number;
  width: number;
  height: number;
  sillHeight: number;
  swing: DoorSwing;
  /** Windows: how many sashes share the opening (default single). */
  windowConfig?: WindowConfiguration;
  /** Windows: how it opens; "" when unstated. */
  windowOperation?: WindowOperation;
  notes?: string;
}

function assertOpeningFits(d: Draft, o: WallOpening): void {
  const w = d.wall(o.wallId);
  const len = d.wallLength(w);
  if (!Number.isInteger(o.offsetFromStart) || !Number.isInteger(o.width) || o.width <= 0 || o.height <= 0 || o.sillHeight < 0) {
    throw new GeometryError("Opening size must be positive.");
  }
  if (o.offsetFromStart < 0 || o.offsetFromStart + o.width > len + 0.5) {
    throw new GeometryError("The opening does not fit within the wall.");
  }
  for (const other of d.openingsOf(o.wallId)) {
    if (other.id === o.id) continue;
    const overlap = o.offsetFromStart < other.offsetFromStart + other.width && other.offsetFromStart < o.offsetFromStart + o.width;
    if (overlap) throw new GeometryError(`That overlaps another ${other.kind} on this wall.`);
  }
}

export function placeOpening(model: PlanModel, ctx: OpContext, wallId: string, input: OpeningInput): OpResult & { openingId: string } {
  const d = new Draft(model);
  if (d.wall(wallId).kind === "boundary") throw new GeometryError("Doors and windows belong on physical walls, not room boundaries.");
  const o: WallOpening = {
    id: ctx.newId(),
    wallId,
    kind: input.kind,
    offsetFromStart: input.offsetFromStart,
    width: input.width,
    height: input.height,
    sillHeight: input.sillHeight,
    swing: input.swing,
    windowConfig: input.windowConfig ?? "single",
    windowOperation: input.windowOperation ?? "",
    originId: null,
    notes: input.notes ?? "",
    archivedAt: null,
  };
  assertOpeningFits(d, o);
  d.setOpening(o);
  return { ...d.result(), openingId: o.id };
}

/** Offset of an opening centred on a point projected onto the wall (used by the Door/Window tools). */
export function openingOffsetForPoint(model: PlanModel, wallId: string, point: Point, width: number): number {
  const w = wallOf(model, wallId);
  const a = nodeOf(model, w.startNodeId);
  const b = nodeOf(model, w.endNodeId);
  const c = closestPointOnSegment(point, a, b);
  const len = distance(a, b);
  const center = distance(a, c.point);
  return Math.max(0, Math.min(Math.round(len - width), Math.round(center - width / 2)));
}

export function moveOpening(model: PlanModel, ctx: OpContext, openingId: string, offsetFromStart: number): OpResult {
  void ctx;
  const d = new Draft(model);
  const o = d.openings.get(openingId);
  if (!o || o.archivedAt !== null) throw new GeometryError("That opening no longer exists.");
  const updated = { ...o, offsetFromStart };
  assertOpeningFits(d, updated);
  d.setOpening(updated);
  return d.result();
}

export function setOpeningProps(
  model: PlanModel,
  ctx: OpContext,
  openingId: string,
  props: Partial<Pick<WallOpening, "width" | "height" | "sillHeight" | "swing" | "kind" | "notes" | "windowConfig" | "windowOperation">>,
): OpResult {
  void ctx;
  const d = new Draft(model);
  const o = d.openings.get(openingId);
  if (!o || o.archivedAt !== null) throw new GeometryError("That opening no longer exists.");
  const updated = { ...o, ...props };
  assertOpeningFits(d, updated);
  d.setOpening(updated);
  return d.result();
}

export function archiveOpening(model: PlanModel, ctx: OpContext, openingId: string): OpResult {
  const d = new Draft(model);
  const o = d.openings.get(openingId);
  if (!o || o.archivedAt !== null) throw new GeometryError("That opening no longer exists.");
  d.setOpening({ ...o, archivedAt: ctx.now() });
  return d.result();
}

/**
 * UNDO A DECISION, NOT A KEYSTROKE (spec §61B).
 *
 * A plan says "this window goes". Weeks later the answer changes back. These
 * operations put the inherited thing back exactly as it was — the entity
 * was archived, never destroyed, so restoring it keeps its identity, its
 * lineage and everything bound to it.
 */
export function restoreWall(model: PlanModel, _ctx: OpContext, wallId: string): OpResult {
  const d = new Draft(model);
  const w = d.walls.get(wallId);
  if (!w) throw new GeometryError("That wall is not part of this plan.");
  if (w.archivedAt === null) return d.result();
  // Its endpoints come back with it, or the wall would hang off nothing.
  for (const nid of [w.startNodeId, w.endNodeId]) {
    const n = d.nodes.get(nid);
    if (n && n.archivedAt !== null) d.setNode({ ...n, archivedAt: null });
  }
  d.setWall({ ...w, archivedAt: null });
  return d.result();
}

export function restoreOpening(model: PlanModel, _ctx: OpContext, openingId: string): OpResult {
  const d = new Draft(model);
  const o = d.openings.get(openingId);
  if (!o) throw new GeometryError("That opening is not part of this plan.");
  if (o.archivedAt === null) return d.result();
  const host = d.walls.get(o.wallId);
  if (!host || host.archivedAt !== null) {
    throw new GeometryError("The wall it belonged to is no longer in this plan — put the wall back first.");
  }
  d.setOpening({ ...o, archivedAt: null });
  return d.result();
}

export function restoreObject(model: PlanModel, _ctx: OpContext, objectId: string): OpResult {
  const d = new Draft(model);
  const o = d.objects.get(objectId);
  if (!o) throw new GeometryError("That object is not part of this plan.");
  if (o.archivedAt === null) return d.result();
  if (o.hostWallId) {
    const host = d.walls.get(o.hostWallId);
    if (!host || host.archivedAt !== null) {
      throw new GeometryError("The wall it was fixed to is no longer in this plan — put the wall back first.");
    }
  }
  d.setObject({ ...o, archivedAt: null });
  return d.result();
}

/**
 * Clear Design (per floor): archive every active wall/boundary/opening/node
 * and remove every room anchor of THIS plan. One operation — undoable in
 * session; identities survive (archive, never delete). Rooms, Features and
 * financial records are untouched (the coordinator nulls geometry-derived
 * quantities through the normal effective-value rules).
 */
export function clearFloorGeometry(model: PlanModel, ctx: OpContext): OpResult {
  const d = new Draft(model);
  const now = ctx.now();
  for (const o of [...d.openings.values()]) if (o.archivedAt === null) d.setOpening({ ...o, archivedAt: now });
  for (const o of d.activeObjects()) d.setObject({ ...o, archivedAt: now });
  for (const w of d.activeWalls()) d.setWall({ ...w, archivedAt: now });
  for (const n of d.activeNodes()) d.setNode({ ...n, archivedAt: now });
  for (const roomId of [...d.anchors.keys()]) d.removeAnchor(roomId);
  return d.result();
}

// ---- Room anchors ---------------------------------------------------------
export function setRoomAnchor(model: PlanModel, ctx: OpContext, roomId: string, point: Point | null): OpResult {
  void ctx;
  const d = new Draft(model);
  if (point === null) {
    d.removeAnchor(roomId);
  } else {
    assertInBounds(point);
    d.setAnchor({ roomId, floorId: model.floorId, x: point.x, y: point.y });
  }
  return d.result();
}

/**
 * KEEP EVERY ROOM LABEL IN ITS OWN ROOM (spec §60B).
 *
 * Walls move; a label that was comfortably in the middle of the kitchen can
 * end up in the hallway, or outside every room. This runs as part of the
 * same commit as the edit that caused it, so the repair shares one undo
 * step with the operation the user performed.
 *
 * It only ever MOVES a label, and only when the room's own region can still
 * be identified from the walls that used to enclose it. A genuine split or
 * merge is left alone and reported by the ordinary conflict/unbound path —
 * naming that room is the user's decision, not a guess.
 */
export function repairRoomAnchors(model: PlanModel, ctx: OpContext, previous: PlanAnalysis): OpResult {
  void ctx;
  const analysis = analyzePlan(model);
  const previousKeyOf = new Map<string, string>();
  for (const [roomId, key] of previous.roomRegion) previousKeyOf.set(roomId, key);

  const states: AnchorState[] = [...model.anchors.values()].map((a) => ({
    roomId: a.roomId,
    point: { x: a.x, y: a.y },
    regionKey: previousKeyOf.get(a.roomId) ?? null,
  }));
  const d = new Draft(model);
  let moved = false;
  for (const decision of reconcileRoomAnchors(analysis, states)) {
    if (decision.action !== "recentre") continue;
    const existing = d.anchors.get(decision.roomId);
    if (!existing) continue;
    if (existing.x === decision.to.x && existing.y === decision.to.y) continue;
    d.setAnchor({ ...existing, x: decision.to.x, y: decision.to.y });
    moved = true;
  }
  return moved ? d.result() : { model, changes: [] };
}

/** Apply a changeset in a direction (used by undo/redo). */
export function applyChanges(model: PlanModel, changes: EntityChange[], direction: "forward" | "backward"): PlanModel {
  const nodes = new Map(model.nodes);
  const walls = new Map(model.walls);
  const openings = new Map(model.openings);
  const objects = new Map(model.objects);
  const anchors = new Map(model.anchors);
  for (const c of changes) {
    const target = direction === "forward" ? c.after : c.before;
    switch (c.kind) {
      case "node":
        if (target) nodes.set(c.id, target as PlanNode);
        else nodes.delete(c.id);
        break;
      case "wall":
        if (target) walls.set(c.id, target as Wall);
        else walls.delete(c.id);
        break;
      case "opening":
        if (target) openings.set(c.id, target as WallOpening);
        else openings.delete(c.id);
        break;
      case "object":
        if (target) objects.set(c.id, target as PlanObject);
        else objects.delete(c.id);
        break;
      case "anchor":
        if (target) anchors.set(c.id, target as RoomAnchor);
        else anchors.delete(c.id);
        break;
    }
  }
  return { designId: model.designId, floorId: model.floorId, nodes, walls, openings, objects, anchors };
}

/** Convenience: current active wall count etc. for tests/UI. */
export function summarize(model: PlanModel): { walls: number; nodes: number; openings: number } {
  return { walls: activeWalls(model).length, nodes: [...model.nodes.values()].filter((n) => n.archivedAt === null).length, openings: activeOpenings(model).length };
}

// ---------------------------------------------------------------------------
// Plan objects (Phase 8)
// ---------------------------------------------------------------------------
export interface CreateObjectInput {
  definitionId: string;
  geometryType: ObjectGeometryType;
  /** point: center; linear/area: run start. */
  at: Point;
  end?: Point | null;
  rotationX10?: number;
  width: number;
  depth: number;
  heightInches?: number | null;
  mountHeightInches?: number | null;
  parentObjectId?: string | null;
  hostWallId?: string | null;
  hostOffset?: number | null;
  hostSide?: 1 | -1 | null;
  roomId?: string | null;
  /** Structured configuration for this object (spec §61D). */
  config?: string | null;
  originId?: string | null;
  notes?: string;
}

function assertObjectValid(o: PlanObject): void {
  if (o.geometryType === "point") {
    if (o.width <= 0 || o.depth <= 0) throw new GeometryError("Object size must be positive.");
  } else {
    if (o.endX === null || o.endY === null) throw new GeometryError("A run needs two end points.");
    if (objectRunLength(o) <= 0) throw new GeometryError("A run cannot have zero length.");
    if (o.depth <= 0) throw new GeometryError("Run depth must be positive.");
  }
  assertInBounds({ x: o.x, y: o.y });
  if (o.endX !== null && o.endY !== null) assertInBounds({ x: o.endX, y: o.endY });
}

export function createObject(model: PlanModel, ctx: OpContext, input: CreateObjectInput): OpResult & { objectId: string } {
  const d = new Draft(model);
  if (input.hostWallId) d.wall(input.hostWallId);
  if (input.parentObjectId) d.object(input.parentObjectId);
  const o: PlanObject = {
    id: ctx.newId(),
    floorId: model.floorId,
    definitionId: input.definitionId,
    geometryType: input.geometryType,
    x: input.at.x,
    y: input.at.y,
    endX: input.end?.x ?? null,
    endY: input.end?.y ?? null,
    rotationX10: input.rotationX10 ?? 0,
    width: input.width,
    depth: input.depth,
    heightInches: input.heightInches ?? null,
    mountHeightInches: input.mountHeightInches ?? null,
    parentObjectId: input.parentObjectId ?? null,
    detached: false,
    hostWallId: input.hostWallId ?? null,
    hostOffset: input.hostOffset ?? null,
    hostSide: input.hostSide ?? null,
    roomId: input.roomId ?? null,
    featureId: null,
    config: input.config ?? null,
    originId: input.originId ?? null,
    notes: input.notes ?? "",
    archivedAt: null,
  };
  assertObjectValid(o);
  d.setObject(o);
  return { ...d.result(), objectId: o.id };
}

/** Move a point object (free: new center; hosted: new offset along the wall). */
export function moveObject(model: PlanModel, ctx: OpContext, objectId: string, to: Point): OpResult {
  void ctx;
  const d = new Draft(model);
  const o = d.object(objectId);
  if (o.hostWallId) {
    const wall = d.wall(o.hostWallId);
    const a = d.node(wall.startNodeId);
    const b = d.node(wall.endNodeId);
    const c = closestPointOnSegment(to, a, b);
    const len = distance(a, b);
    const n = { x: -(b.y - a.y) / len, y: (b.x - a.x) / len };
    const side: 1 | -1 = (to.x - c.point.x) * n.x + (to.y - c.point.y) * n.y >= 0 ? 1 : -1;
    d.setObject({ ...o, hostOffset: Math.round(len * c.t), hostSide: side });
    return d.result();
  }
  if (o.geometryType === "point") {
    assertInBounds(to);
    d.setObject({ ...o, x: to.x, y: to.y });
  } else {
    const dx = to.x - Math.round((o.x + (o.endX ?? o.x)) / 2);
    const dy = to.y - Math.round((o.y + (o.endY ?? o.y)) / 2);
    const moved = { ...o, x: o.x + dx, y: o.y + dy, endX: (o.endX ?? o.x) + dx, endY: (o.endY ?? o.y) + dy };
    assertObjectValid(moved);
    d.setObject(moved);
  }
  syncDerivedChildren(d, objectId);
  return d.result();
}

export function rotateObject(model: PlanModel, ctx: OpContext, objectId: string, rotationX10: number): OpResult {
  void ctx;
  const d = new Draft(model);
  const o = d.object(objectId);
  if (o.geometryType !== "point") throw new GeometryError("Rotate the run by moving its end points.");
  d.setObject({ ...o, rotationX10: ((Math.round(rotationX10) % 3600) + 3600) % 3600 });
  return d.result();
}

/** Resize footprint (point: width/depth; linear/area: depth; length via endpoints). */
export function resizeObject(model: PlanModel, ctx: OpContext, objectId: string, size: { width?: number; depth?: number }): OpResult {
  void ctx;
  const d = new Draft(model);
  const o = d.object(objectId);
  const updated: PlanObject = { ...o, width: size.width ?? o.width, depth: size.depth ?? o.depth, detached: o.parentObjectId ? true : o.detached };
  assertObjectValid(updated);
  d.setObject(updated);
  syncDerivedChildren(d, objectId);
  return d.result();
}

export function createRunObject(
  model: PlanModel,
  ctx: OpContext,
  input: Omit<CreateObjectInput, "geometryType" | "end"> & { end: Point; geometryType?: "linear" | "area" },
): OpResult & { objectId: string } {
  return createObject(model, ctx, { ...input, geometryType: input.geometryType ?? "linear", end: input.end });
}

export function moveRunEndpoint(model: PlanModel, ctx: OpContext, objectId: string, which: "start" | "end", to: Point): OpResult {
  void ctx;
  const d = new Draft(model);
  const o = d.object(objectId);
  if (o.geometryType === "point") throw new GeometryError("Only runs have end points.");
  assertInBounds(to);
  const updated = which === "start" ? { ...o, x: to.x, y: to.y } : { ...o, endX: to.x, endY: to.y };
  if (o.parentObjectId) updated.detached = true;
  assertObjectValid(updated);
  d.setObject(updated);
  syncDerivedChildren(d, objectId);
  return d.result();
}

/** Set a run's exact length keeping the START point fixed. */
export function setRunLength(model: PlanModel, ctx: OpContext, objectId: string, lengthPu: number): OpResult {
  void ctx;
  if (!Number.isInteger(lengthPu) || lengthPu <= 0) throw new GeometryError("Length must be positive.");
  const d = new Draft(model);
  const o = d.object(objectId);
  if (o.geometryType === "point" || o.endX === null || o.endY === null) throw new GeometryError("Only runs have a length.");
  const to = pointAlong({ x: o.x, y: o.y }, { x: o.endX, y: o.endY }, lengthPu);
  assertInBounds(to);
  const updated = { ...o, endX: to.x, endY: to.y, detached: o.parentObjectId ? true : o.detached };
  d.setObject(updated);
  syncDerivedChildren(d, objectId);
  return d.result();
}

/** Keep derived children (countertops) in sync with their parent run until detached. */
function syncDerivedChildren(d: Draft, parentId: string): void {
  const parent = d.objects.get(parentId);
  if (!parent || parent.archivedAt !== null || parent.geometryType === "point") return;
  for (const child of d.activeObjects()) {
    if (child.parentObjectId !== parentId || child.detached) continue;
    d.setObject({ ...child, x: parent.x, y: parent.y, endX: parent.endX, endY: parent.endY });
  }
}

export function setObjectRoom(model: PlanModel, ctx: OpContext, objectId: string, roomId: string | null): OpResult {
  void ctx;
  const d = new Draft(model);
  d.setObject({ ...d.object(objectId), roomId });
  return d.result();
}

/** Scope binding: link/unlink the object to a Project Feature. */
export function setObjectFeature(model: PlanModel, ctx: OpContext, objectId: string, featureId: string | null): OpResult {
  void ctx;
  const d = new Draft(model);
  d.setObject({ ...d.object(objectId), featureId });
  return d.result();
}

export function setObjectDimension(
  model: PlanModel,
  ctx: OpContext,
  objectId: string,
  props: Partial<Pick<PlanObject, "heightInches" | "mountHeightInches" | "notes">>,
): OpResult {
  void ctx;
  const d = new Draft(model);
  d.setObject({ ...d.object(objectId), ...props });
  return d.result();
}

/** Archive the object in THIS design only (root/other-scenario copies untouched). */
/**
 * Set the structured configuration of a placed object (spec §61D) — a
 * vanity's sink arrangement, a corner cabinet's shape. The DEFINITION says
 * what the value means; geometry does not interpret it.
 */
export function setObjectConfig(model: PlanModel, _ctx: OpContext, objectId: string, config: string | null): OpResult {
  const d = new Draft(model);
  const o = d.object(objectId);
  d.setObject({ ...o, config });
  return d.result();
}

export function archiveObject(model: PlanModel, ctx: OpContext, objectId: string): OpResult {
  const d = new Draft(model);
  const now = ctx.now();
  const o = d.object(objectId);
  d.setObject({ ...o, archivedAt: now });
  // derived children (countertop on a deleted run) survive but detach
  for (const child of d.activeObjects()) {
    if (child.parentObjectId === objectId) d.setObject({ ...child, detached: true });
  }
  return d.result();
}
